<!-- Parsec Cloud (https://parsec.cloud) Copyright (c) AGPL-3.0-only 2016-present Scille SAS -->

<template>
  <div
    class="choose-auth-page"
    :class="isWeb() ? 'choose-auth-page--web' : ''"
  >
    <div
      class="choose-auth-list"
      v-if="authentication === undefined"
    >
      <ion-text
        class="body choose-auth-list__label"
        v-show="showTitle"
      >
        {{ $msTranslate('Authentication.description') }}
      </ion-text>
      <ion-radio-group
        v-model="authentication"
        class="radio-list"
        :class="isWeb() ? 'radio-list--web' : ''"
        @ion-change="onChange"
      >
        <ion-radio
          class="item-radio radio-list-item"
          label-placement="end"
          justify="start"
          :value="DevicePrimaryProtectionStrategyTag.Password"
          :disabled="authMethodIsDisabled(DevicePrimaryProtectionStrategyTag.Password)"
        >
          <authentication-card
            @click="onMethodSelected(DevicePrimaryProtectionStrategyTag.Password)"
            :state="getAuthCardState(DevicePrimaryProtectionStrategyTag.Password)"
            :auth-method="DevicePrimaryProtectionStrategyTag.Password"
          />
        </ion-radio>
        <ion-radio
          class="item-radio radio-list-item"
          label-placement="end"
          justify="start"
          :value="DevicePrimaryProtectionStrategyTag.Keyring"
          :disabled="authMethodIsDisabled(DevicePrimaryProtectionStrategyTag.Keyring)"
        >
          <authentication-card
            :auth-method="DevicePrimaryProtectionStrategyTag.Keyring"
            :state="getAuthCardState(DevicePrimaryProtectionStrategyTag.Keyring)"
          />
        </ion-radio>

        <ion-radio
          class="item-radio radio-list-item"
          label-placement="end"
          justify="start"
          :value="DevicePrimaryProtectionStrategyTag.PKI"
          :disabled="authMethodIsDisabled(DevicePrimaryProtectionStrategyTag.PKI)"
        >
          <authentication-card
            @click="onMethodSelected(DevicePrimaryProtectionStrategyTag.PKI)"
            :state="getAuthCardState(DevicePrimaryProtectionStrategyTag.PKI)"
            :auth-method="DevicePrimaryProtectionStrategyTag.PKI"
          />
        </ion-radio>

        <ion-radio
          class="item-radio radio-list-item"
          label-placement="end"
          :value="DevicePrimaryProtectionStrategyTag.OpenBao"
          justify="start"
          :disabled="authMethodIsDisabled(DevicePrimaryProtectionStrategyTag.OpenBao)"
        >
          <authentication-card
            :state="getAuthCardState(DevicePrimaryProtectionStrategyTag.OpenBao)"
            @click="onMethodSelected(DevicePrimaryProtectionStrategyTag.OpenBao)"
            :auth-method="DevicePrimaryProtectionStrategyTag.OpenBao"
          />
        </ion-radio>
      </ion-radio-group>
    </div>

    <div
      class="choose-auth-choice"
      v-else
    >
      <div v-if="authentication === DevicePrimaryProtectionStrategyTag.Keyring">
        <div class="method-chosen">
          <authentication-card
            :auth-method="DevicePrimaryProtectionStrategyTag.Keyring"
            :state="querying ? AuthenticationCardState.Disabled : AuthenticationCardState.Update"
            @update-clicked="changeAuthenticationMethod"
          />
        </div>
        <keyring-information />
      </div>

      <div v-if="authentication === DevicePrimaryProtectionStrategyTag.Password">
        <div class="method-chosen">
          <authentication-card
            :auth-method="DevicePrimaryProtectionStrategyTag.Password"
            :state="querying ? AuthenticationCardState.Disabled : AuthenticationCardState.Update"
            @update-clicked="changeAuthenticationMethod"
          />
        </div>
        <ms-choose-password-input
          ref="choosePassword"
          class="choose-password"
          @on-enter-keyup="$emit('enterPressed')"
        />
      </div>

      <div v-if="authentication === DevicePrimaryProtectionStrategyTag.PKI">
        <div class="method-chosen">
          <authentication-card
            :auth-method="DevicePrimaryProtectionStrategyTag.PKI"
            :state="querying ? AuthenticationCardState.Disabled : AuthenticationCardState.Update"
            @update-clicked="changeAuthenticationMethod"
          />
        </div>
        <certificate-selection
          :purpose="CertificatePurpose.Encrypt"
          @certificate-selected="certif = $event"
        />
      </div>

      <div v-if="authentication === DevicePrimaryProtectionStrategyTag.OpenBao && serverConfig?.openbao && openBaoAuthAvailable">
        <div class="method-chosen">
          <authentication-card
            :auth-method="DevicePrimaryProtectionStrategyTag.OpenBao"
            :state="querying ? AuthenticationCardState.Disabled : AuthenticationCardState.Update"
            @update-clicked="changeAuthenticationMethod()"
          />
        </div>
        <div class="provider-card">
          <sso-provider-card
            v-for="auth in serverConfig?.openbao.auths.filter((auth) => isSSOProviderHandled(auth.tag))"
            :key="auth.tag"
            :provider="auth.tag"
            :is-connected="openBaoClient !== undefined"
            @sso-selected="onSSOLoginClicked"
          />
          <ms-spinner
            v-if="querying"
            class="provider-card-spinner"
          />
        </div>
      </div>

      <span
        class="form-error subtitles-sm"
        v-show="error"
      >
        {{ $msTranslate(error) }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import KeyringInformation from '@/components/devices/KeyringInformation.vue';
import SsoProviderCard from '@/components/devices/SsoProviderCard.vue';
import CertificateSelection from '@/components/misc/CertificateSelection.vue';
import authenticationCard from '@/components/profile/AuthenticationCard.vue';
import { AuthenticationCardState } from '@/components/profile/types';
import {
  CertificatePurpose,
  CertificateWithDetailsValid,
  DevicePrimaryProtectionStrategyTag,
  DeviceSaveStrategy,
  OpenBaoAuthConfigTag,
  ParsecAddr,
  PrimaryProtectionStrategy,
  ServerConfig,
  constructSaveStrategy,
  isKeyringAvailable,
  isSmartcardAvailable,
  isWeb,
  openCertificate,
} from '@/parsec';
import { OpenBaoClient, OpenBaoErrorType, isSSOProviderHandled, openBaoConnect } from '@/services/openBao';
import { IonRadio, IonRadioGroup, IonText } from '@ionic/vue';
import { MsChoosePasswordInput, MsSpinner } from 'megashark-lib';
import { computed, nextTick, onMounted, ref, useTemplateRef } from 'vue';

const authentication = ref<DevicePrimaryProtectionStrategyTag | undefined>(undefined);
const keyringAvailable = ref(false);
const choosePasswordRef = useTemplateRef<InstanceType<typeof MsChoosePasswordInput>>('choosePassword');
const certif = ref<CertificateWithDetailsValid | undefined>(undefined);
const openBaoClient = ref<undefined | OpenBaoClient>(undefined);
const smartcardAvailable = ref(false);
const querying = ref(false);

const error = ref('');

const openBaoAuthAvailable = computed(() => {
  return props.serverConfig?.openbao && props.serverConfig?.openbao.auths.some((auth) => isSSOProviderHandled(auth.tag));
});

const props = defineProps<{
  showTitle?: boolean;
  activeAuth?: DevicePrimaryProtectionStrategyTag;
  serverConfig?: ServerConfig;
  serverAddr: ParsecAddr;
}>();

defineExpose({
  areFieldsCorrect,
  authentication,
  getSaveStrategy,
});

const emits = defineEmits<{
  (e: 'authReset'): void;
  (e: 'enterPressed'): void;
}>();

onMounted(async () => {
  keyringAvailable.value = props.activeAuth === DevicePrimaryProtectionStrategyTag.Keyring ? true : await isKeyringAvailable();
  smartcardAvailable.value = await isSmartcardAvailable();
});

async function onChange(_value: any): Promise<void> {
  if (authentication.value === DevicePrimaryProtectionStrategyTag.Password && choosePasswordRef.value) {
    await choosePasswordRef.value.setFocus();
  }
}

function getAuthCardState(auth: DevicePrimaryProtectionStrategyTag): AuthenticationCardState {
  if (props.serverConfig && !props.serverConfig.isAuthMethodAllowed(auth)) {
    return AuthenticationCardState.Forbidden;
  }
  switch (auth) {
    case DevicePrimaryProtectionStrategyTag.Password:
      return AuthenticationCardState.Default;
    case DevicePrimaryProtectionStrategyTag.Keyring:
      if (!keyringAvailable.value) {
        return AuthenticationCardState.Unavailable;
      }
      return auth === props.activeAuth ? AuthenticationCardState.Active : AuthenticationCardState.Default;
    case DevicePrimaryProtectionStrategyTag.PKI:
      if (!smartcardAvailable.value) {
        return AuthenticationCardState.Unavailable;
      }
      return auth === props.activeAuth ? AuthenticationCardState.Active : AuthenticationCardState.Default;
    case DevicePrimaryProtectionStrategyTag.OpenBao:
      if (!openBaoAuthAvailable.value) {
        return AuthenticationCardState.Unavailable;
      }
      return auth === props.activeAuth ? AuthenticationCardState.Active : AuthenticationCardState.Default;
    default:
      return AuthenticationCardState.Default;
  }
}

function authMethodIsDisabled(auth: DevicePrimaryProtectionStrategyTag): boolean {
  const state = getAuthCardState(auth);
  return [AuthenticationCardState.Disabled, AuthenticationCardState.Forbidden, AuthenticationCardState.Unavailable].includes(state);
}

async function onMethodSelected(method: DevicePrimaryProtectionStrategyTag): Promise<void> {
  authentication.value = method;
  error.value = '';
  if (method === DevicePrimaryProtectionStrategyTag.Password) {
    await nextTick();
    if (choosePasswordRef.value) {
      choosePasswordRef.value.setFocus();
    }
  }
}

async function changeAuthenticationMethod(): Promise<void> {
  authentication.value = undefined;
  certif.value = undefined;
  error.value = '';
  emits('authReset');
}

async function getSaveStrategy(): Promise<DeviceSaveStrategy | undefined> {
  if (authentication.value === DevicePrimaryProtectionStrategyTag.Keyring) {
    return constructSaveStrategy(PrimaryProtectionStrategy.useKeyring());
  } else if (authentication.value === DevicePrimaryProtectionStrategyTag.OpenBao) {
    if (!openBaoClient.value) {
      window.nativeAPI.log('error', 'Selected auth is openBao but no client available');
      return undefined;
    }
    return constructSaveStrategy(PrimaryProtectionStrategy.useOpenBao(openBaoClient.value.getConnectionInfo()));
  } else if (authentication.value === DevicePrimaryProtectionStrategyTag.PKI) {
    if (certif.value?.encryptCert) {
      const certResult = await openCertificate(certif.value.encryptCert.reference);
      if (certResult.ok) {
        return constructSaveStrategy(PrimaryProtectionStrategy.useSmartcard(certResult.value));
      }
    }
    return undefined;
  } else if (choosePasswordRef.value?.password) {
    return constructSaveStrategy(PrimaryProtectionStrategy.usePassword(choosePasswordRef.value?.password));
  }
  return undefined;
}

async function areFieldsCorrect(): Promise<boolean> {
  if (keyringAvailable.value && authentication.value === DevicePrimaryProtectionStrategyTag.Keyring) {
    return true;
  } else if (authentication.value === DevicePrimaryProtectionStrategyTag.Password && choosePasswordRef.value) {
    return await choosePasswordRef.value.areFieldsCorrect();
  } else if (authentication.value === DevicePrimaryProtectionStrategyTag.OpenBao && openBaoClient.value !== undefined) {
    return true;
  } else if (authentication.value === DevicePrimaryProtectionStrategyTag.PKI) {
    return certif.value !== undefined;
  }
  return false;
}

async function onSSOLoginClicked(provider: OpenBaoAuthConfigTag): Promise<void> {
  if (querying.value) {
    window.nativeAPI.log('warn', 'Clicked on SSO login while already login in');
    return;
  }
  if (!props.serverConfig?.openbao) {
    error.value = 'Authentication.openBaoUnavailable';
    window.nativeAPI.log('error', 'OpenBao not enabled on this server');
    return;
  }
  const auth = props.serverConfig.openbao.auths.find((auth) => auth.tag === provider);
  if (!auth) {
    error.value = 'Authentication.invalidOpenBaoData';
    window.nativeAPI.log('error', `Provider '${provider}' selected but is not available in server config`);
    return;
  }
  try {
    querying.value = true;
    const result = await openBaoConnect(
      props.serverConfig.openbao.serverUrl,
      auth.tag,
      auth.mountPath,
      props.serverConfig.openbao.secret.mountPath,
      props.serverConfig.openbao.transitMountPath,
    );
    if (!result.ok) {
      if (result.error.type === OpenBaoErrorType.PopupFailed) {
        error.value = 'Authentication.popupBlocked';
      } else {
        error.value = 'Authentication.invalidOpenBaoData';
      }
      window.nativeAPI.log('error', `Error while connecting with SSO: ${JSON.stringify(result.error)}`);
    } else {
      openBaoClient.value = result.value;
    }
  } finally {
    querying.value = false;
  }
}
</script>

<style scoped lang="scss">
.choose-auth-list {
  padding: 0 0 0.25rem;

  &__label {
    color: var(--parsec-color-light-primary-700);
    margin-bottom: 1rem;
    display: block;
  }

  .radio-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  // eslint-disable-next-line vue-scoped-css/no-unused-selector
  .item-radio {
    z-index: 2;

    &.radio-disabled {
      &::part(container) {
        opacity: 0.3;
      }

      &::part(label) {
        opacity: 1;
        display: flex;
        gap: 0.125rem;
      }
    }

    &::part(mark) {
      background: none;
      transition: none;
      transform: none;
    }

    &::part(label) {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      margin: 0;
      overflow: visible;
      width: 100%;
    }

    &::part(container) {
      display: none;
    }
  }
}

.choose-auth-choice {
  .method-chosen {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-bottom: 1.5rem;

    &__title {
      color: var(--parsec-color-light-secondary-hard-grey);
    }
  }

  .choose-password {
    padding: 1.5rem 1rem;
    border: 1px solid var(--parsec-color-light-secondary-medium);
    border-radius: var(--parsec-radius-12) 0 var(--parsec-radius-12) var(--parsec-radius-12);
    box-shadow: var(--parsec-shadow-soft);
    background: var(--parsec-color-light-secondary-inversed-contrast);

    @include ms.responsive-breakpoint('sm') {
      border-top: 1px solid var(--parsec-color-light-secondary-medium);
    }
  }
}

.choose-auth-page--web {
  padding: 0;

  .choose-password {
    border-radius: none;
    border: none;
    box-shadow: none;
    padding: 0 0.25rem;
    background: none;
  }
}

.provider-card {
  position: relative;
  margin-top: 1.25rem;

  .provider-card-spinner {
    position: absolute;
    top: 2rem;
    right: 2rem;
    transform: translate(-50%, -50%);
  }
}
</style>
